/**
 * Landing page entry point.
 *
 * Lightweight: no PixiJS, no game code. Only i18n + scroll animations + atmospheric effects.
 */

import { CLASS_COLORS } from '@shared/config/class-colors';

import { escapeHtml } from '../client/ui/escape-html';
import { logoSvg } from '../client/ui/logo';

type Locale = 'de' | 'en';
type TranslationMap = Record<string, string>;

const LOCALE_STORAGE_KEY = 'zk-locale';
const SUPPORTED_LOCALES: readonly Locale[] = ['de', 'en'] as const;

/**
 * Locales are lazy chunks — the landing page fetches the active one and never
 * the other (#599). Shared with the game page, so switching language there and
 * coming back costs no second download.
 */
const LOADERS: Record<Locale, () => Promise<{ default: TranslationMap }>> = {
  de: () => import('../client/i18n/de.json') as Promise<{ default: TranslationMap }>,
  en: () => import('../client/i18n/en.json') as Promise<{ default: TranslationMap }>,
};

const translations: Partial<Record<Locale, TranslationMap>> = {};

async function loadLocale(locale: Locale): Promise<void> {
  if (translations[locale]) return;
  translations[locale] = (await LOADERS[locale]()).default;
}

function detectLocale(): Locale {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
    return stored as Locale;
  }
  const browserLang = navigator.language.split('-')[0];
  if (SUPPORTED_LOCALES.includes(browserLang as Locale)) {
    return browserLang as Locale;
  }
  return 'de';
}

let activeLocale: Locale = detectLocale();

function t(key: string): string {
  return translations[activeLocale]?.[key] ?? key;
}

async function setLocale(locale: Locale): Promise<void> {
  await loadLocale(locale);
  activeLocale = locale;
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  render();
  renderLeaderboardSection();
}

// ── Auth State ──

let authenticatedUser: { username: string } | null = null;

async function checkAuth(): Promise<void> {
  try {
    const response = await fetch('/api/v1/auth/me');
    if (response.ok) {
      const body = (await response.json()) as { data: { username: string } | null };
      authenticatedUser = body.data;
      render();
    }
  } catch {
    // Ignore — guest user
  }
}

// ── Leaderboard Top 3 ──

interface TopScore {
  nickname: string;
  wave: number;
  kills: number;
}

let topScores: TopScore[] = [];

async function fetchTopScores(): Promise<void> {
  try {
    const response = await fetch('/api/v1/highscores?limit=5&sort=wave');
    if (response.ok) {
      const body = (await response.json()) as { data: TopScore[] };
      topScores = body.data;
      renderLeaderboardSection();
    }
  } catch {
    // Ignore — leaderboard section stays with empty message
  }
}

function renderLeaderboardSection(): void {
  const container = document.getElementById('landing-leaderboard-body');
  if (!container) return;

  if (topScores.length === 0) {
    container.innerHTML = `<p class="landing-lb-empty">${t('landing.leaderboard.empty')}</p>`;
    return;
  }

  const medals = ['\u{1F947}', '\u{1F948}', '\u{1F949}'];
  container.innerHTML = topScores
    .map(
      (s, i) => `
    <div class="landing-lb-row">
      <span class="landing-lb-medal">${medals[i] ?? String(i + 1)}</span>
      <span class="landing-lb-name">${escapeHtml(s.nickname)}</span>
      <span class="landing-lb-stat">${t('landing.leaderboard.wave')} ${String(s.wave)}</span>
      <span class="landing-lb-stat">${String(s.kills)} ${t('landing.leaderboard.kills')}</span>
    </div>
  `,
    )
    .join('') + `<a href="/play" class="landing-lb-viewall">${t('landing.leaderboard.viewAll')}</a>`;
}


// ── Render Helpers ──

function renderCtaButtons(): string {
  if (authenticatedUser) {
    return `
      <a href="/play" class="menu-btn menu-btn-primary landing-cta-play" data-testid="cta-play">
        ${t('landing.cta.continue')}
      </a>
    `;
  }
  return `
    <a href="/play" class="menu-btn menu-btn-primary landing-cta-play" data-testid="cta-play">
      ${t('landing.cta.play')}
    </a>
    <a href="/play?auth=register" class="menu-btn menu-btn-ghost landing-cta-secondary" data-testid="cta-register">
      ${t('landing.cta.register')}
    </a>
    <a href="/play?auth=login" class="landing-login-link" data-testid="cta-login">
      ${t('landing.cta.login')}
    </a>
  `;
}

function renderParticles(): string {
  const particles: string[] = [];
  for (let i = 0; i < 25; i++) {
    const left = Math.floor(Math.random() * 100);
    const top = Math.floor(20 + Math.random() * 70);
    const delay = (Math.random() * 8).toFixed(1);
    const size = 2 + Math.floor(Math.random() * 3);
    const isRed = Math.random() > 0.7;
    const color = isRed ? 'var(--danger-primary)' : 'var(--accent-primary)';
    particles.push(
      `<div class="landing-particle" style="left:${String(left)}%;top:${String(top)}%;animation-delay:${delay}s;width:${String(size)}px;height:${String(size)}px;background:${color}"></div>`,
    );
  }
  return particles.join('');
}

function highlightCard(keyPrefix: string, icon: string): string {
  return `
    <div class="landing-highlight-card" data-reveal>
      <div class="landing-highlight-icon">${icon}</div>
      <div class="landing-highlight-text">
        <h3 class="landing-highlight-title">${t(`${keyPrefix}.title`)}</h3>
        <p class="landing-highlight-desc">${t(`${keyPrefix}.desc`)}</p>
      </div>
    </div>
  `;
}

function featureTile(keyPrefix: string, icon: string): string {
  return `
    <div class="landing-tile" data-reveal>
      <span class="landing-tile-icon">${icon}</span>
      <h3 class="landing-tile-title">${t(`${keyPrefix}.title`)}</h3>
      <p class="landing-tile-desc">${t(`${keyPrefix}.desc`)}</p>
    </div>
  `;
}

/**
 * Gameplay showcase — real captures, not a diagram.
 *
 * This used to be a hand-built SVG of grey boxes and red dots. It described the
 * game accurately and sold it not at all: a visitor cannot tell from a wireframe
 * what the game *feels* like. These are screenshots from an actual run, fog and
 * HUD included, because that is what the player will see.
 */
function renderGameplayShots(): string {
  const shots = [
    { file: 'gameplay-lab', key: 'landing.shot.lab' },
    { file: 'gameplay-hive', key: 'landing.shot.hive' },
  ];
  return shots.map((sh) => `
    <figure class="landing-shot" data-reveal>
      <img src="/images/landing/${sh.file}.webp" alt="${t(`${sh.key}.alt`)}"
           loading="lazy" decoding="async" width="1120" height="630">
      <figcaption class="landing-shot-caption">${t(sh.key)}</figcaption>
    </figure>
  `).join('');
}

/**
 * Boss gallery — the splash art, with the in-game intro video on hover.
 *
 * The videos already ship for the boss cinematic, so this costs no new assets.
 * They are `preload="none"` and only start on hover: a landing page must not
 * pull three videos before the visitor has asked for anything.
 */
function renderBossGallery(): string {
  const bosses = ['brute', 'necromancer', 'hivemind'];
  return bosses.map((b) => `
    <figure class="landing-boss" data-reveal data-boss="${b}">
      <div class="landing-boss-media">
        <img src="/images/boss-${b}.webp" alt="${t(`landing.boss.${b}.alt`)}"
             loading="lazy" decoding="async" width="1280" height="714">
        <video class="landing-boss-video" muted loop playsinline preload="none"
               src="/videos/boss-intro-${b}.mp4" aria-hidden="true"></video>
      </div>
      <figcaption>
        <span class="landing-boss-name">${t(`landing.boss.${b}.name`)}</span>
        <span class="landing-boss-desc">${t(`landing.boss.${b}.desc`)}</span>
      </figcaption>
    </figure>
  `).join('');
}

function renderBloodSplatter(): string {
  const splatters: string[] = [];
  const positions = [[8, 15], [25, 45], [48, 72], [72, 28], [88, 58], [35, 88], [62, 12], [15, 65]];
  for (const [x, y] of positions) {
    const size = 20 + Math.floor(Math.random() * 40);
    const rot = Math.floor(Math.random() * 360);
    splatters.push(
      `<svg viewBox="0 0 60 60" style="position:absolute;left:${String(x)}%;top:${String(y)}%;width:${String(size)}px;height:${String(size)}px;transform:rotate(${String(rot)}deg);opacity:0.06;pointer-events:none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="30" cy="30" r="12" fill="#cc3333"/>
        <circle cx="22" cy="20" r="6" fill="#cc3333"/><circle cx="40" cy="22" r="5" fill="#cc3333"/>
        <circle cx="18" cy="36" r="4" fill="#cc3333"/><circle cx="42" cy="38" r="7" fill="#cc3333"/>
        <ellipse cx="30" cy="46" rx="8" ry="3" fill="#cc3333"/>
      </svg>`,
    );
  }
  return splatters.join('');
}

function renderCharacterPreview(): string {
  // Inline SVG icons for character classes (landing page is a separate bundle, no @shared imports)
  const svgIcons: Record<string, string> = {
    soldier: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2.5"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>',
    medic: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 2h6v7h7v6h-7v7H9v-7H2V9h7V2z" opacity="0.9"/></svg>',
    scout: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2 L22 12 L12 22 L2 12 Z"/><circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.2"/><circle cx="12" cy="12" r="1.8" fill="currentColor"/></svg>',
    engineer: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/><circle cx="12" cy="12" r="3"/></svg>',
    demolitions: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2 L14 8 L20 6 L16 11 L22 12 L16 14 L19 20 L13 16 L12 22 L11 16 L5 20 L8 14 L2 12 L8 11 L4 6 L10 8 Z" opacity="0.85"/></svg>',
  };
  // Colours come from the shared source, not a fourth copy of the palette.
  const classes = (Object.keys(CLASS_COLORS) as (keyof typeof CLASS_COLORS)[]).map((id) => ({
    id,
    color: CLASS_COLORS[id],
    bg: `${CLASS_COLORS[id]}22`,
  }));
  // The painted portraits already exist for the in-game character select; the
  // landing page showed a 40 px icon instead. The icon stays as a badge on top
  // of the portrait so the class is still readable at a glance.
  const cards = classes.map((c) => `
    <figure class="landing-class-card" data-reveal style="--card-accent:${c.color};border-color:${c.color}44">
      <div class="landing-class-portrait">
        <img src="/images/class-${c.id}.webp" alt="${t(`character.class.${c.id}`)}"
             loading="lazy" decoding="async" width="512" height="768">
        <span class="landing-class-icon" style="color:${c.color};background:${c.bg};border-color:${c.color}33">${svgIcons[c.id] ?? ''}</span>
      </div>
      <figcaption>
        <span class="landing-class-name">${t(`character.class.${c.id}`)}</span>
        <span class="landing-class-passive">${t(`character.desc.${c.id}`)}</span>
      </figcaption>
    </figure>
  `).join('');
  return `
    <section class="landing-classes" aria-label="${t('menu.classPreview')}">
      <h2 class="landing-section-title">${t('landing.classes.label')}</h2>
      <div class="landing-classes-grid">${cards}</div>
    </section>
  `;
}

/**
 * The campaign as seven pictures instead of seven words.
 *
 * The briefing artwork is generated for the in-game mission select and was
 * unused here; a bullet list of mission names told a visitor nothing about the
 * arc from a deserted street to the hive.
 */
function renderCampaignMissions(): string {
  const missions = [
    { id: 'ground_zero', key: 'landing.campaign.mission1' },
    { id: 'supply_run', key: 'landing.campaign.mission2' },
    { id: 'the_barricade', key: 'landing.campaign.mission3' },
    { id: 'clear_the_path', key: 'landing.campaign.mission4' },
    { id: 'the_hive', key: 'landing.campaign.mission5' },
    { id: 'last_stand', key: 'landing.campaign.mission6' },
    { id: 'the_swarm', key: 'landing.campaign.mission7' },
  ];
  const cards = missions.map((m, i) => `
    <figure class="landing-mission" data-reveal>
      <img src="/images/campaign/mission-${m.id}.webp" alt="${t(m.key)}"
           loading="lazy" decoding="async" width="960" height="540">
      <figcaption>
        <span class="landing-mission-no">${String(i + 1).padStart(2, '0')}</span>
        <span class="landing-mission-name">${t(m.key)}</span>
      </figcaption>
    </figure>
  `).join('');
  return `<div class="landing-mission-strip">${cards}</div>`;
}

// ── Main Render ──

function render(): void {
  const root = document.getElementById('landing-root');
  if (!root) return;

  document.documentElement.lang = activeLocale;

  root.innerHTML = `
    <div class="landing-page">
      <div class="landing-vignette"></div>
      <div class="landing-blood-decor">${renderBloodSplatter()}</div>

      <!-- Header -->
      <header class="landing-header">
        <button id="lang-toggle" class="landing-lang-btn" aria-label="${t('landing.header.langToggle')}">
          ${activeLocale === 'de' ? 'EN' : 'DE'}
        </button>
      </header>

      <!-- Hero -->
      <section class="landing-hero">
        <div class="landing-hero-bg"></div>
        <div class="landing-particles">${renderParticles()}</div>
        <div class="landing-hero-logo">${logoSvg('72px', '#44cc44')}</div>
        <h1 class="landing-title">${t('landing.hero.title')}</h1>
        <p class="landing-tagline">${t('landing.hero.tagline')}</p>
        <p class="landing-subtitle">${t('landing.hero.subtitle')}</p>
        <div class="landing-cta-group">
          ${renderCtaButtons()}
        </div>
        <p class="landing-desktop-hint">${t('landing.desktop.hint')}</p>
      </section>

      <!-- Stats Bar -->
      <div class="landing-stats-bar" data-reveal>
        <div class="landing-stat">${t('landing.stats.weapons')}</div>
        <div class="landing-stat-divider"></div>
        <div class="landing-stat">${t('landing.stats.zombieTypes')}</div>
        <div class="landing-stat-divider"></div>
        <div class="landing-stat">${t('landing.stats.bosses')}</div>
        <div class="landing-stat-divider"></div>
        <div class="landing-stat">${t('landing.stats.difficulties')}</div>
      </div>

      <!-- Gameplay showcase (real captures) -->
      <section class="landing-gameplay-preview" data-reveal aria-label="${t('landing.showcase.label')}">
        <h2 class="landing-section-title">${t('landing.showcase.label')}</h2>
        <p class="landing-section-lead">${t('landing.showcase.horde.sub')}</p>
        <div class="landing-shots">${renderGameplayShots()}</div>
      </section>

      <!-- Bosses -->
      <section class="landing-bosses" data-reveal aria-label="${t('landing.bosses.label')}">
        <h2 class="landing-section-title">${t('landing.bosses.label')}</h2>
        <p class="landing-section-lead">${t('landing.bosses.lead')}</p>
        <div class="landing-boss-grid">${renderBossGallery()}</div>
      </section>

      <!-- Campaign (compact) -->
      <section class="landing-campaign landing-campaign-compact" aria-label="${t('landing.campaign.label')}" data-reveal>
        <h2 class="landing-section-title">${t('landing.campaign.label')}</h2>
        <p class="landing-campaign-desc">${t('landing.campaign.desc')}</p>
        ${renderCampaignMissions()}
      </section>

      <!-- Character Preview -->
      ${renderCharacterPreview()}

      <!-- Highlights -->
      <section class="landing-highlights" aria-label="${t('landing.highlight.label')}">
        <h2 class="landing-section-title">${t('landing.highlight.label')}</h2>
        <div class="landing-highlights-grid">
          ${highlightCard('landing.highlight.1', '\u{1F9DF}')}
          ${highlightCard('landing.highlight.2', '\u{2694}\u{FE0F}')}
          ${highlightCard('landing.highlight.3', '\u{1F480}')}
          ${highlightCard('landing.highlight.4', '\u{1F91D}')}
        </div>
      </section>

      <!-- Features -->
      <section class="landing-features" aria-label="${t('landing.features.label')}">
        <h2 class="landing-section-title">${t('landing.features.label')}</h2>
        <div class="landing-grid">
          ${featureTile('landing.feature.1', '\u{25A6}')}
          ${featureTile('landing.feature.2', '\u{1F47E}')}
          ${featureTile('landing.feature.3', '\u{25B2}')}
          ${featureTile('landing.feature.4', '\u{2620}')}
          ${featureTile('landing.feature.5', '\u{2605}')}
          ${featureTile('landing.feature.6', '\u{25CE}')}
        </div>
      </section>

      <!-- Leaderboard Top 5 -->
      <section class="landing-leaderboard" data-reveal>
        <h2 class="landing-section-title">${t('landing.leaderboard.label')}</h2>
        <div id="landing-leaderboard-body" class="landing-lb-body">
          <p class="landing-lb-empty">${t('landing.leaderboard.empty')}</p>
        </div>
      </section>

      <!-- Final CTA -->
      <section class="landing-final-cta" data-reveal>
        <p class="landing-final-text">${t('landing.final.cta')}</p>
        <a href="/play" class="menu-btn menu-btn-primary landing-cta-play landing-final-btn" data-testid="cta-play-bottom">
          ${t('landing.cta.play')}
        </a>
      </section>

      <!-- Footer -->
      <footer class="landing-footer">
        <span>${t('landing.footer.text')}</span>
        <span class="landing-footer-credits">
          ${t('landing.footer.credits.label')}: K3H3, Fetzen, Skankhunt 42, xmpx
        </span>
        <span class="landing-footer-legal">
          <a href="/impressum">Impressum</a>
          <a href="/datenschutz">Datenschutz</a>
        </span>
      </footer>
    </div>
  `;

  // Wire language toggle
  document.getElementById('lang-toggle')?.addEventListener('click', () => {
    void setLocale(activeLocale === 'de' ? 'en' : 'de');
  });

  // Scroll-reveal for feature tiles and sections
  initScrollReveal();
  initBossHoverVideos();

  // Populate leaderboard after render
  renderLeaderboardSection();
}

// ── Scroll Reveal ──

/**
 * Play a boss intro while the pointer rests on its card.
 *
 * `preload="none"` until then — three autoplaying videos on a landing page is
 * bandwidth the visitor never asked for. Pointer events rather than `:hover`
 * so a touch device simply keeps the still image.
 */
function initBossHoverVideos(): void {
  for (const fig of document.querySelectorAll<HTMLElement>('.landing-boss')) {
    const video = fig.querySelector('video');
    if (!video) continue;
    fig.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'touch') return;
      video.preload = 'auto';
      void video.play().catch(() => undefined);
      fig.classList.add('is-playing');
    });
    fig.addEventListener('pointerleave', () => {
      video.pause();
      video.currentTime = 0;
      fig.classList.remove('is-playing');
    });
  }
}

function initScrollReveal(): void {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tiles = document.querySelectorAll('[data-reveal]');

  if (prefersReduced) {
    tiles.forEach((tile) => {
      tile.classList.add('landing-tile-visible');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('landing-tile-visible');
          observer.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.15 },
  );

  tiles.forEach((tile) => {
    observer.observe(tile);
  });
}

// ── Init ──

async function init(): Promise<void> {
  await loadLocale(activeLocale);
  render();
  void checkAuth();
  void fetchTopScores();
}

void init();
